import { FALLBACK_ERROR_MESSAGE, extractApiError } from '@frontend/duck-tape';
import type { CreateInviteFormSchema } from '@frontend/forms';
import { useEditProfileForm } from '@frontend/forms/settings/editProfileForm';
import type { CommonModalProps } from '@frontend/web-react';
import { BirthdayInput, Button, Modal, PhoneNumberInput, TextInput, YStack, showToast } from '@frontend/web-react';
import { useEditProfileMutation, useRetrieveClientQuery } from '@frontend/web/hooks';
import { handleMutation } from '@frontend/web/utils';

export const UpdateProfileModal = ({ isOpen, onClose }: CommonModalProps) => {
  const { data: client } = useRetrieveClientQuery();
  const [editProfile, { isLoading }] = useEditProfileMutation();

  const handleEditProfile = ({ email: _email, ...rest }: CreateInviteFormSchema) => {
    handleMutation({
      mutation: () => editProfile(rest).unwrap(),
      onError: (error) => {
        const errorString = extractApiError(error);
        const uniquenessError = errorString?.toLowerCase().includes('phone number: this field must be unique');
        return showToast({
          title: uniquenessError ? 'This phone number is already in use.' : errorString ?? FALLBACK_ERROR_MESSAGE,
          variant: 'danger',
        });
      },
      onSuccess: onClose,
      showErrorToast: false,
      successMessage: 'Profile updated',
    });
  };
  const { dirty, getControl, handleSubmit, isValid } = useEditProfileForm({
    initialValues: {
      birthday: (client?.birthday || '') as DateISOString,
      email: client?.email || '',
      firstName: client?.firstName || '',
      lastName: client?.lastName || '',
      nickname: client?.nickname || '',
      phoneNumber: client?.phoneNumber || '',
    },
    onSubmit: handleEditProfile,
    validateOnMount: false,
  });

  return (
    <Modal headerGap="none" isOpen={isOpen} onClose={onClose} title="Edit profile" titlesAlignment="start">
      <form onSubmit={handleSubmit}>
        <YStack gapY="lg">
          <YStack gapY="md">
            <TextInput {...getControl('firstName')} label="First name" />
            <TextInput {...getControl('lastName')} label="Last name" />
            <TextInput {...getControl('nickname')} label="Nickname" />
            <BirthdayInput
              {...getControl('birthday')}
              onChange={(val) => getControl('birthday').onChange(val as DateISOString)}
            />
            <PhoneNumberInput {...getControl('phoneNumber')} />
            <TextInput {...getControl('email')} disabled label="Email" />
            <Button disabled={!isValid || !dirty} fullWidth isForm isLoading={isLoading} label="Submit" />
          </YStack>
        </YStack>
      </form>
    </Modal>
  );
};
